import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Textarea } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useCreateSpace } from '@/hooks/useSpaceManagement';
import { routes } from '@/lib/routes';

interface CreateSpaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateSpaceModal({ isOpen, onClose }: CreateSpaceModalProps) {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const createSpace = useCreateSpace();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setName('');
    setDescription('');
  };

  const handleClose = () => {
    if (createSpace.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const space = await createSpace.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
      });
      success(`Created ${space.name}`);
      reset();
      onClose();
      navigate(routes.console.spaceRoot(space.spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create space');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Space"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={createSpace.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={createSpace.isPending}
            disabled={!name.trim()}
          >
            Create Space
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Marketing"
          autoFocus
          maxLength={80}
        />
        <Textarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this space for?"
          rows={3}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground">
          You'll be the Owner of this space. Add members from the Space → Members tab.
        </p>
      </div>
    </Modal>
  );
}
